import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { BULK_PRICES, ViewerGroupInterface } from '../../core';
import styles from './MenuGroups.module.scss';
import styled, { css } from 'styled-components';
import { ArrowLeftSolid, ArrowRightSolid } from '../../icons';

export const Icon = styled.div<{ hoverable?: boolean }>`
  display: inline-block;
  width: 18px;
  height: 18px;
  cursor: pointer;
  ​
    ${(props) =>
      props.hoverable &&
      `
    @media(hover) {
      &:hover {
        opacity: 0.5
      }
    }
  `}
    ​
    svg {
    fill: currentColor;
    width: 100%;
    height: 100%;
  }
`;

const ArrowCss = css`
  left: 10px;
  top: 5px;
  border-radius: 30px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
`;
export const ArrowLeft = styled.div`
  ${ArrowCss};
`;
export const ArrowRight = styled.div`
  ${ArrowCss};
  left: auto;
  right: 10px;
`;
export const ArrowLeftIconStyled = styled(Icon)`
  font-size: 22px;
`;
export const ArrowRightIconStyled = styled(Icon)`
  font-size: 22px;
`;

export interface MenuGroupsProps {
  groups: ViewerGroupInterface[];
  selectedGroupId: string | null;
  onSelect: (group: ViewerGroupInterface) => void;
}

const MenuGroups: React.FC<MenuGroupsProps> = (props) => {
  const [t] = useTranslation();
  const [displayArrow, setDisplayArrow] = useState(false);
  const groupsItemRef = React.useRef<HTMLDivElement>(null);
  const onGroupClick = (grp: ViewerGroupInterface): void => {
    if (grp.id !== props.selectedGroupId) {
      props.onSelect(grp);
    }
  };

  const scrollRight = () => {
    if (groupsItemRef.current) {
      groupsItemRef.current.scrollBy({
        left: 100,
        behavior: 'smooth',
      });
    }
  };

  const scrollLeft = () => {
    if (groupsItemRef.current) {
      groupsItemRef.current.scrollBy({
        left: -100,
        behavior: 'smooth',
      });
    }
  };

  const orderGBulkLast = (groups: ViewerGroupInterface[]): ViewerGroupInterface[] => {
    return groups.sort((a, b) => {
      if (a.id === BULK_PRICES.BULK_PRICES_GROUP_CODE) {
        return 1;
      }
      if (b.id === BULK_PRICES.BULK_PRICES_GROUP_CODE) {
        return -1;
      }
      return a.displayOrder - b.displayOrder;
    });
  };

  useEffect(() => {
    const updateDisplayArrow = () => {
      if (groupsItemRef.current) {
        let groupsRealWidth = groupsItemRef.current.scrollWidth;
        let groupsDisplayedWidth = groupsItemRef.current.clientWidth;

        if (groupsRealWidth > groupsDisplayedWidth) {
          setDisplayArrow(true);
        } else {
          setDisplayArrow(false);
        }
      }
    };

    // Measure width initially
    updateDisplayArrow();

    // Add event listener to update width on window resize
    window.addEventListener('resize', updateDisplayArrow);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', updateDisplayArrow);
    };
  }, []);

  //if groups are more than one, then map
  if (props.groups.length > 1) {
    return (
      <div className={styles.ff_menu_groups}>
        {displayArrow && (
          <div className={styles.ff_menu_groups__arrow_left}>
            <ArrowLeft onClick={scrollLeft}>
              <ArrowLeftIconStyled>
                <ArrowLeftSolid />
              </ArrowLeftIconStyled>
            </ArrowLeft>
          </div>
        )}
        <div className={styles.ff_menu_group} data-testid='ff-menu-group' ref={groupsItemRef}>
          {orderGBulkLast(props.groups).map((group, i) => {
            return (
              group.id !== 'Other' && (
                <div
                  key={group.id}
                  className={classnames(styles.ff_menu_item, {
                    [styles.ff_menu_item__selected]: group.id === props.selectedGroupId,
                  })}
                  onClick={() => onGroupClick(group)}
                >
                  {t(group.id)}
                </div>
              )
            );
          })}
        </div>
        {displayArrow && (
          <div className={styles.ff_menu_groups__arrow_right}>
            <ArrowRight onClick={scrollRight}>
              <ArrowLeftIconStyled>
                <ArrowRightSolid />
              </ArrowLeftIconStyled>
            </ArrowRight>
          </div>
        )}
      </div>
    );

    //otherwise there is no need
  } else {
    return <div className={styles.ff_menu_group} data-testid='ff-menu-group'></div>;
  }
};

export default MenuGroups;
